/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  VStack,
  Image,
  Progress,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

// Logo importları
import LogoColored from "assets/svg/logo-colored.svg";
import LogoWhite from "assets/svg/logo-white.svg";
import { useColorMode } from "@chakra-ui/react";

/**
 * Splash Screen Komponenti
 * Yüklenme durumlarında logo ve progress bar gösterir
 * 
 * @param {boolean} isLoading - Yüklenme durumu
 * @param {string} message - Gösterilecek mesaj (opsiyonel)
 * @param {number} progress - Progress bar değeri (0-100, opsiyonel)
 * @param {boolean} fullScreen - Tam ekran mı yoksa parent container'a göre mi (default: true)
 */
export default function SplashScreen({ 
  isLoading = false, 
  message = "Yükleniyor...",
  progress = null,
  fullScreen = true
}) {
  const { colorMode } = useColorMode();
  const logoImage = colorMode === "dark" ? LogoWhite : LogoColored;
  const containerRef = useRef(null);
  const [logoSize, setLogoSize] = useState(fullScreen ? 80 : 30);
  
  // ✅ Tüm hook'ları early return'den önce çağır
  const bgColor = useColorModeValue("white", "navy.800");
  const overlayBg = useColorModeValue("rgba(255, 255, 255, 0.95)", "rgba(26, 32, 44, 0.95)");
  const textColor = useColorModeValue("gray.700", "gray.300");
  const progressColor = useColorModeValue("blue.500", "blue.300");
  const progressBg = useColorModeValue("gray.200", "gray.700");

  // Container boyutuna göre logo boyutunu ayarla (sadece fullScreen=false için)
  useEffect(() => {
    if (fullScreen || !containerRef.current) {
      return;
    }

    const updateLogoSize = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerHeight = container.offsetHeight;
      const containerWidth = container.offsetWidth;
      
      // Container boyutuna göre logo boyutunu hesapla
      // Küçük container (< 200px): 32px (tam gözüksün)
      // Orta container (200-400px): 40-45px
      // Büyük container (> 400px): 50-60px
      let newSize = 32; // Küçük tablolar için 32px (tam gözüksün)
      
      if (containerHeight > 400 || containerWidth > 600) {
        newSize = 60; // Büyük tablo
      } else if (containerHeight > 300 || containerWidth > 500) {
        newSize = 50; // Orta-büyük tablo
      } else if (containerHeight > 200 || containerWidth > 350) {
        newSize = 40; // Orta tablo
      } else {
        newSize = 32; // Küçük tablo (tam gözüksün)
      }
      
      setLogoSize(newSize);
    };

    updateLogoSize();
    
    // Resize observer ile container boyut değişikliklerini takip et
    const resizeObserver = new ResizeObserver(updateLogoSize);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [fullScreen, isLoading]);

  if (!isLoading) {
    return null;
  }

  const currentLogoSize = fullScreen ? 80 : logoSize;
  const minLogoWidth = fullScreen ? "200px" : `${Math.max(85, logoSize * 2.1)}px`;
  const minLogoHeight = fullScreen ? "80px" : `${Math.max(32, logoSize + 2)}px`;

  return (
    <Box
      ref={containerRef}
      position={fullScreen ? "fixed" : "absolute"}
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg={fullScreen ? bgColor : overlayBg}
      zIndex={fullScreen ? 9999 : 100}
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius={!fullScreen ? "md" : undefined}
    >
      <VStack spacing={fullScreen ? 6 : 2.5} align="center" maxW="400px" px={fullScreen ? 8 : 4} mt={fullScreen ? "-10vh" : "8px"}>
        {/* Logo - Her zaman göster */}
        <Box
          mb={fullScreen ? 4 : 0.5}
          animation="pulse 2s ease-in-out infinite"
          minH={minLogoHeight}
          minW={minLogoWidth}
          h={minLogoHeight}
          w="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          pt={fullScreen ? 0 : "2px"}
          sx={{
            "@keyframes pulse": {
              "0%, 100%": {
                opacity: 1,
              },
              "50%": {
                opacity: 0.8,
              },
            },
          }}
        >
          <Image
            src={logoImage}
            alt="ixPilot Logo"
            h={`${currentLogoSize}px`}
            w="auto"
            maxW="85%"
            maxH="95%"
            objectFit="contain"
            display="block"
            flexShrink={0}
          />
        </Box>

        {/* Progress Bar */}
        <Box w="100%" maxW={fullScreen ? "300px" : "220px"} mt={fullScreen ? 0 : -1}>
          <Progress
            value={progress !== null ? progress : undefined}
            isIndeterminate={progress === null}
            colorScheme="blue"
            size={fullScreen ? "md" : "sm"}
            borderRadius="full"
            bg={progressBg}
            sx={{
              "& > div": {
                bg: progressColor,
              },
            }}
          />
        </Box>

        {/* Mesaj */}
        <Text
          fontSize={fullScreen ? "sm" : "xs"}
          color={textColor}
          fontWeight="medium"
          textAlign="center"
          px={2}
          mt={fullScreen ? 0 : -0.5}
        >
          {message}
        </Text>
      </VStack>
    </Box>
  );
}

